import { useMemo } from 'react'

import {
  TicketingMyTeamsDocument,
  type TicketingMyTeamsQuery,
  type TicketingMyTeamsQueryVariables,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

export interface TeamOption {
  label: string
  value: string
}

export const useMyTeams = (): {
  teams: TeamOption[]
  loading: boolean
  error?: unknown
} => {
  const { employee } = useCurrentSession()

  const { data, loading, error } = useQuery<TicketingMyTeamsQuery, TicketingMyTeamsQueryVariables>(
    TicketingMyTeamsDocument,
    {
      variables: { employeeIds: [employee.id] },
      fetchPolicy: 'cache-and-network',
    }
  )

  const teams = useMemo<TeamOption[]>(() => {
    const edges = data?.teams?.membershipsConnection?.edges ?? []
    const seen = new Set<string>()

    return edges.reduce<TeamOption[]>((acc, edge) => {
      const team = edge.node?.team
      if (team?.id && team?.name && !seen.has(team.id)) {
        seen.add(team.id)
        acc.push({ label: team.name, value: team.id })
      }
      return acc
    }, [])
  }, [data?.teams?.membershipsConnection?.edges])

  return { teams, loading, error }
}
